// retail-opening — c04 Bozor variant.
//
// "ATLAS BOUTIQUE" — a fictional Tashkent boutique grand opening on
// 20.09.2026. The brief is chic + retail/hospitality energy with a clear
// ribbon-cutting moment. Cream-peach ground (#FFF5E8), dark earth (#2A1F18)
// type, terracotta (#C8804B) ribbon and accents, Bodoni Moda for display.
//
// SIGNATURE — ribbon-cut animation on hero load:
//   A terracotta ribbon SVG is drawn full-bleed across the hero, anchored
//   diagonally over the storefront photo. ~1.1s after the hero mounts, the
//   ribbon "cuts": the upper half slides up-left and the lower half slides
//   down-right via clip-path/transform, while a single `ding` (scissors
//   snip) plays. prefers-reduced-motion shows the ribbon already cut and
//   suppresses the snip. The cut state stays — it's the brand's opening
//   moment, captured statically afterwards.
//
// Sections (in order):
//   1. Hero — full-bleed storefront photo, ribbon overlay, brand name,
//      ribbon-cut signature, opening countdown band
//   2. CountdownSection
//   3. StorySection — Uzbek copy: ATLAS BOUTIQUE vision, fashion, customer
//   4. UNIQUE — Collection Preview: 5 lookbook items, name (Bodoni italic)
//      + price range, terracotta hairlines between rows
//   5. GallerySection (interior + lookbook photos from data.ts)
//   6. ScheduleSection — 12:00 ribbon cut, 12:30 first guests, 14:00
//      cocktail, 16:00 trunk show, 19:00 after-party
//   7. MapSection
//   8. RsvpSection — label="Birinchi mehmonlarimiz boʻling"
//   9. ShareSection
//  10. GuestFooter
//
// Sound: 5 triggers, all gated by useSound's persisted preference.
//   • `ding`   — scissors-snip on the ribbon-cut moment (signature)
//   • `chime`  — once when RSVP turns "yes"
//   • `click`  — RSVP yes/no/maybe and music toggle
//   • `swoosh` — first time the collection preview reveals into view
//   • `tap`    — collection-row hover/click
//
// Music: a small floating "♪" toggle bottom-right. We don't ship an audio
// asset — instead an ambient lounge-flavoured pad is synthesised through
// Web Audio (warm minor seventh chord, slow tremolo) so it loads instantly
// and stays on-brand. Defaults OFF; both the toggle and the global mute
// gate the loop.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-driven reveal ────────────────────────────────

interface RevealProps {
  children: ReactNode;
  delay?: number;
  reduce: boolean;
  onShow?: () => void;
}

function Reveal({ children, delay = 0, reduce, onShow }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  const fired = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!fired.current) {
              fired.current = true;
              onShow?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onShow]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: reduce
          ? "none"
          : `opacity 1.0s ease-out ${delay}ms, transform 1.0s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── SIGNATURE — the cut-the-ribbon ribbon ──────────────────────────────
//
// We render two SVG halves of a single diagonal ribbon. On `cut`, they
// translate apart (upper half up-left, lower half down-right) via CSS
// transforms, with the scissors briefly snapping at the centre.
function HeroRibbon({
  color,
  ink,
  cut,
  reduce,
}: {
  color: string;
  ink: string;
  cut: boolean;
  reduce: boolean;
}) {
  // Each half draws the same trapezoidal ribbon shape but masked to its
  // half via an SVG clipPath. We rely on transform + opacity for the cut
  // animation — clip-path could work too, but transforms give us the
  // satisfying drift outward.
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      {/* UPPER half */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          transform: cut
            ? "translate(-3%, -4%) rotate(-2deg)"
            : "translate(0, 0) rotate(0)",
          transition: reduce
            ? "none"
            : "transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          willChange: reduce ? undefined : "transform",
        }}
      >
        <defs>
          <clipPath id="atlas-ribbon-upper" clipPathUnits="userSpaceOnUse">
            {/* Everything above the centre cut line — a slanted half-plane */}
            <polygon points="-10,-10 220,-10 220,196 -10,66" />
          </clipPath>
        </defs>
        <g clipPath="url(#atlas-ribbon-upper)">
          {/* Soft fold shadow */}
          <path d="M -10 70 L 220 200 L 220 222 L -10 92 Z" fill={ink} opacity="0.10" />
          {/* Ribbon body */}
          <path d="M -10 60 L 220 190 L 220 218 L -10 88 Z" fill={color} opacity="0.94" />
          {/* Inner highlight */}
          <path d="M -10 64 L 220 194 L 220 200 L -10 70 Z" fill="white" opacity="0.18" />
        </g>
      </svg>

      {/* LOWER half */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          transform: cut
            ? "translate(3%, 4%) rotate(2deg)"
            : "translate(0, 0) rotate(0)",
          transition: reduce
            ? "none"
            : "transform 900ms cubic-bezier(0.22, 0.61, 0.36, 1)",
          willChange: reduce ? undefined : "transform",
        }}
      >
        <defs>
          <clipPath id="atlas-ribbon-lower" clipPathUnits="userSpaceOnUse">
            <polygon points="-10,66 220,196 220,290 -10,290" />
          </clipPath>
        </defs>
        <g clipPath="url(#atlas-ribbon-lower)">
          <path d="M -10 70 L 220 200 L 220 222 L -10 92 Z" fill={ink} opacity="0.10" />
          <path d="M -10 60 L 220 190 L 220 218 L -10 88 Z" fill={color} opacity="0.94" />
          <path d="M -10 64 L 220 194 L 220 200 L -10 70 Z" fill="white" opacity="0.18" />
        </g>
      </svg>

      {/* Scissors snap — fades in just before the cut, fades out after */}
      <svg
        viewBox="0 0 24 24"
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 48,
          height: 48,
          transform: "translate(-50%, -50%)",
          opacity: reduce ? 0 : cut ? 0 : 0,
          // (visual only — see <style> keyframes "atlasScissors" below)
          animation: reduce
            ? "none"
            : "atlasScissors 1100ms ease-out 600ms 1",
        }}
        aria-hidden
      >
        <circle cx="5" cy="7" r="2.4" fill="none" stroke={ink} strokeWidth="1.2" />
        <circle cx="5" cy="17" r="2.4" fill="none" stroke={ink} strokeWidth="1.2" />
        <path d="M 7 7 L 21 12" stroke={ink} strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <path d="M 7 17 L 21 12" stroke={ink} strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <circle cx="9.4" cy="12" r="0.9" fill={ink} />
      </svg>
    </div>
  );
}

// ─── Hero countdown — small terracotta band under the brand ────────────

interface MiniCountdownProps {
  ink: string;
  accent: string;
  font: string;
  days: number;
  hours: number;
  mins: number;
  secs: number;
  labels: { d: string; h: string; m: string; s: string };
}

function MiniCountdown({ ink, accent, font, days, hours, mins, secs, labels }: MiniCountdownProps) {
  const Cell = ({ value, label }: { value: number; label: string }) => (
    <div style={{ textAlign: "center", minWidth: 64 }}>
      <div
        style={{
          fontFamily: font,
          fontWeight: 400,
          fontSize: 36,
          lineHeight: 1,
          color: ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
          letterSpacing: "0.02em",
        }}
      >
        {value.toString().padStart(2, "0")}
      </div>
      <div
        style={{
          marginTop: 6,
          fontFamily: "var(--font-ui)",
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.32em",
          color: accent,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
    </div>
  );
  return (
    <div
      className="atlas-mini-countdown"
      style={{
        display: "flex",
        gap: 22,
        justifyContent: "center",
        alignItems: "center",
        padding: "16px 22px",
        border: `1px solid ${accent}66`,
        background: "rgba(255,255,255,0.55)",
        backdropFilter: "blur(2px)",
      }}
    >
      <Cell value={days} label={labels.d} />
      <span style={{ width: 1, height: 32, background: `${accent}66` }} aria-hidden />
      <Cell value={hours} label={labels.h} />
      <span style={{ width: 1, height: 32, background: `${accent}66` }} aria-hidden />
      <Cell value={mins} label={labels.m} />
      <span style={{ width: 1, height: 32, background: `${accent}66` }} aria-hidden />
      <Cell value={secs} label={labels.s} />
    </div>
  );
}

// ─── Music — synthesised lounge ambient pad ─────────────────────────────
//
// Warm minor-seventh pad on a sine + triangle pair, with a slow tremolo so
// the chord breathes. Quiet, on-brand. Toggled on demand and gated by the
// global useSound mute.

const PAD_NOTES = [261.63, 311.13, 392.0, 466.16]; // C, Eb, G, Bb (Cm7)

function useLoungePad(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const stopRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (stopRef.current) {
        stopRef.current();
        stopRef.current = null;
      }
      if (ctxRef.current && ctxRef.current.state === "running") {
        void ctxRef.current.suspend().catch(() => undefined);
      }
      return;
    }

    let cancelled = false;
    const start = async () => {
      if (!ctxRef.current) {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        try {
          ctxRef.current = new Ctor();
        } catch {
          return;
        }
      }
      const c = ctxRef.current;
      if (!c) return;
      if (c.state === "suspended") {
        try {
          await c.resume();
        } catch {
          return;
        }
      }
      if (cancelled) return;

      const master = c.createGain();
      master.gain.value = 0.0001;
      master.connect(c.destination);
      // Slow fade-in
      const t0 = c.currentTime;
      master.gain.exponentialRampToValueAtTime(0.05, t0 + 1.2);

      // Tremolo LFO — slow amplitude wobble for a breathing pad feel
      const lfo = c.createOscillator();
      const lfoGain = c.createGain();
      lfo.frequency.value = 0.18; // very slow
      lfoGain.gain.value = 0.018;
      lfo.connect(lfoGain);
      lfoGain.connect(master.gain);
      lfo.start();

      // Sustained chord oscillators — sine + triangle stack per note
      const oscs: OscillatorNode[] = [];
      for (const f of PAD_NOTES) {
        for (const type of ["sine", "triangle"] as const) {
          const o = c.createOscillator();
          o.type = type;
          o.frequency.value = type === "triangle" ? f * 0.5 : f; // octave below for warmth
          const g = c.createGain();
          g.gain.value = type === "triangle" ? 0.18 : 0.32;
          o.connect(g);
          g.connect(master);
          o.start();
          oscs.push(o);
        }
      }

      stopRef.current = () => {
        const tEnd = c.currentTime;
        try {
          master.gain.cancelScheduledValues(tEnd);
          master.gain.setValueAtTime(master.gain.value, tEnd);
          master.gain.exponentialRampToValueAtTime(0.0001, tEnd + 0.6);
        } catch {
          /* noop */
        }
        window.setTimeout(() => {
          for (const o of oscs) {
            try {
              o.stop();
              o.disconnect();
            } catch {
              /* noop */
            }
          }
          try {
            lfo.stop();
            lfo.disconnect();
            lfoGain.disconnect();
            master.disconnect();
          } catch {
            /* noop */
          }
        }, 700);
      };
    };
    void start();

    return () => {
      cancelled = true;
      if (stopRef.current) {
        stopRef.current();
        stopRef.current = null;
      }
    };
  }, [enabled]);

  // Final teardown
  useEffect(() => {
    return () => {
      if (stopRef.current) stopRef.current();
      if (ctxRef.current) {
        try {
          void ctxRef.current.close();
        } catch {
          /* noop */
        }
      }
    };
  }, []);
}

// ─── Collection preview — 5 lookbook items, hardcoded Uzbek ─────────────

const COLLECTION: Array<{ name: string; price: string; note: string }> = [
  {
    name: "Atlas Edit",
    price: "1 200 000 – 2 400 000 soʻm",
    note: "Marg'ilon atlasidan zamonaviy palto va ko'ylaklar",
  },
  {
    name: "The Silk Set",
    price: "850 000 – 1 600 000 soʻm",
    note: "Ipak shol va sharflar — qo'lda bo'yalgan, cheklangan tirajda",
  },
  {
    name: "Modern Adras",
    price: "950 000 – 1 800 000 soʻm",
    note: "Adras matosida tikilgan zamonaviy ofis va kunduzgi liboslar",
  },
  {
    name: "Evening Capsule",
    price: "2 800 000 – 5 200 000 soʻm",
    note: "Maxsus marosimlar uchun kechki kollektsiya",
  },
  {
    name: "Atelier Accessories",
    price: "320 000 – 1 100 000 soʻm",
    note: "Charm sumkalar, kashta belbog'lar va do'ppi-broshlar",
  },
];

// ─── Component ──────────────────────────────────────────────────────────

export function RetailOpeningVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const tt = useT();
  const { play, enabled: soundEnabled } = useSound();

  // Ribbon cut state — flips ~700ms after mount; reduced-motion is true
  // immediately so the user sees the post-cut composition.
  const [cut, setCut] = useState<boolean>(reduce);
  const dingFired = useRef(false);

  useEffect(() => {
    if (reduce) {
      setCut(true);
      return;
    }
    const id = window.setTimeout(() => {
      setCut(true);
      // Sound trigger — scissors snip on the cut moment
      if (!dingFired.current) {
        dingFired.current = true;
        play("ding");
      }
    }, 950);
    return () => window.clearTimeout(id);
  }, [reduce, play]);

  // Music toggle — independent from global mute
  const [music, setMusic] = useState<boolean>(false);
  useLoungePad(music && soundEnabled);
  const toggleMusic = useCallback(() => {
    setMusic((prev) => {
      const next = !prev;
      play(next ? "music-on" : "music-off");
      return next;
    });
  }, [play]);

  // Wrap RSVP setter — yes plays a chime
  const wrappedRsvp = useMemo(
    () => ({
      ...ctx.rsvp,
      setRsvp: (v: string | null) => {
        const before = ctx.rsvp.rsvp;
        ctx.rsvp.setRsvp(v);
        play("click");
        if (v === "yes" && before !== "yes") play("chime");
      },
    }),
    [ctx.rsvp, play],
  );

  // Palette
  const cream = t.bg;
  const earth = t.ink;
  const terracotta = t.accent;
  const heroPhoto = t.photos[0];
  const interiorPhoto = t.photos[1] ?? t.photos[0];

  // Countdown labels
  const labels = {
    d: tt("countdown.days", "KUN"),
    h: tt("countdown.hours", "SOAT"),
    m: tt("countdown.minutes", "DAQ"),
    s: tt("countdown.seconds", "SON"),
  };

  return (
    <>
      {/* ── Local CSS ──────────────────────────────────────────────── */}
      <style>{`
        @keyframes atlasScissors {
          0%   { opacity: 0; transform: translate(-50%, -50%) rotate(0deg) scale(0.8); }
          30%  { opacity: 1; transform: translate(-50%, -50%) rotate(-12deg) scale(1.05); }
          55%  { opacity: 1; transform: translate(-50%, -50%) rotate(8deg)   scale(1.0); }
          100% { opacity: 0; transform: translate(-50%, -50%) rotate(0deg)   scale(0.95); }
        }
        @keyframes atlasFadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .atlas-fade-up { animation: none !important; opacity: 1 !important; transform: none !important; }
        }

        .atlas-row {
          transition: background-color .25s ease-out, transform .25s ease-out;
        }
        .atlas-row:hover,
        .atlas-row:focus-within {
          background: ${terracotta}10;
          transform: translateX(2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .atlas-row:hover, .atlas-row:focus-within { transform: none; }
        }

        .atlas-music-btn { transition: transform .25s ease-out, box-shadow .25s ease-out; }
        .atlas-music-btn:hover { transform: translateY(-1px); }
        @media (prefers-reduced-motion: reduce) {
          .atlas-music-btn:hover { transform: none; }
        }

        .atlas-row:focus-visible,
        .atlas-music-btn:focus-visible {
          outline: 2px solid ${terracotta};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .atlas-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
            text-align: center !important;
          }
          .atlas-hero h1 { font-size: clamp(36px, 12vw, 72px) !important; }
          .atlas-hero-eyebrow { font-size: 9px !important; letter-spacing: 0.32em !important; }
          .atlas-hero-meta { font-size: 11px !important; letter-spacing: 0.26em !important; }
          .atlas-mini-countdown { gap: 10px !important; padding: 12px 14px !important; }
          .atlas-mini-countdown > div { min-width: 48px !important; }
          .atlas-collection-row { grid-template-columns: 1fr !important; gap: 4px !important; }
          .atlas-collection-price { text-align: left !important; white-space: normal !important; word-break: break-word !important; }
          .atlas-music-toggle { right: 16px !important; bottom: 16px !important; }
          .atlas-collection-section { padding: 64px 18px !important; }
          button.atlas-row { min-height: 44px; }
        }
      `}</style>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        className="atlas-hero"
        style={{
          minHeight: "100vh",
          padding: "120px 32px 96px",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          background: cream,
          color: earth,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Full-bleed storefront photo */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: heroPhoto ? `url("${heroPhoto}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.55,
            filter: "saturate(0.9) contrast(0.98)",
          }}
        />
        {/* Cream wash to keep type legible */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${cream}cc 0%, ${cream}99 35%, ${cream}e6 100%)`,
          }}
        />
        {/* Dark-earth hairline frame */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: "min(40px, 6vw)",
            border: `1px solid ${earth}33`,
            pointerEvents: "none",
          }}
        />

        {/* SIGNATURE — diagonal ribbon (animates "cut" on mount) */}
        <HeroRibbon color={terracotta} ink={earth} cut={cut} reduce={reduce} />

        <div style={{ position: "relative", maxWidth: 880, margin: "0 auto", zIndex: 2 }}>
          <div
            className="atlas-hero-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: terracotta,
              marginBottom: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
            }}
          >
            <span style={{ width: 28, height: 1, background: terracotta }} />
            GRAND OPENING
            <span style={{ width: 28, height: 1, background: terracotta }} />
          </div>

          <h1
            style={{
              margin: 0,
              fontFamily: t.font || "var(--font-display)",
              fontWeight: 400,
              fontSize: "clamp(48px, 10vw, 120px)",
              lineHeight: 1,
              letterSpacing: "0.04em",
              color: earth,
              textShadow: `0 1px 0 ${cream}aa`,
            }}
          >
            {t.names}
          </h1>

          <div
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: 22,
              letterSpacing: "0.04em",
              color: terracotta,
              marginTop: 22,
            }}
          >
            Boutique &amp; Atelier — Tashkent
          </div>

          {/* Date · venue keyline */}
          <div style={{ margin: "32px auto 32px", maxWidth: 380 }}>
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: terracotta, opacity: 0.65, marginBottom: 12 }}
            />
            <div
              className="atlas-hero-meta"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.32em",
                color: earth,
                fontFeatureSettings: '"tnum" 1, "lnum" 1',
              }}
            >
              {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
            </div>
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: terracotta, opacity: 0.65, marginTop: 12 }}
            />
          </div>

          {/* Mini countdown band */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <MiniCountdown
              ink={earth}
              accent={terracotta}
              font={t.font || "var(--font-display)"}
              days={ctx.countdown.days}
              hours={ctx.countdown.hours}
              mins={ctx.countdown.mins}
              secs={ctx.countdown.secs}
              labels={labels}
            />
          </div>
        </div>
      </section>

      {/* Standard countdown section below — keeps the section grammar
          consistent with other variants. */}
      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* ─── STORY ──────────────────────────────────────────────────── */}
      <StorySection
        t={t}
        eyebrow="Bizning hikoyamiz"
        title="ATLAS BOUTIQUE — atlas an'analari, zamonaviy uslub"
        body={
          "ATLAS BOUTIQUE Toshkent yuragida ochilayotgan butik va atelye. Biz Marg'ilon atlasi, Buxoro adrasi va o'zbek hunarmandchiligini zamonaviy kesim, yengil siluet va halol mato bilan birlashtiramiz. Har bir libos cheklangan tirajda — yaratuvchilarimiz Toshkent va Farg'onadan, ipak esa hududning eng nozik to'qimachilaridan keladi. Bizning maqsadimiz — har bir mijoz uchun shaxsiy uslub yaratish, mijozni har doim birinchi o'ringa qo'yish va kiyimni an'ana, hunar va ehtirom tilida so'zlatish. Sizni ochilish kuni birinchi mehmonlarimiz bo'lishga chorlaymiz."
        }
      />

      {/* ─── UNIQUE — Collection Preview ────────────────────────────── */}
      <section
        className="atlas-collection-section"
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${earth}22`,
          background: `${terracotta}08`,
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <Reveal reduce={reduce} onShow={() => play("swoosh")}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: terracotta,
                  marginBottom: 16,
                }}
              >
                LOOKBOOK · OCHILISH KOLLEKTSIYASI
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  margin: 0,
                  color: earth,
                  letterSpacing: "-0.005em",
                }}
              >
                Birinchi kollektsiya
              </h2>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: terracotta,
                  margin: "20px auto 0",
                }}
              />
              <p
                style={{
                  marginTop: 20,
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  lineHeight: 1.6,
                  color: `${earth}99`,
                  fontStyle: "italic",
                }}
              >
                Ochilish kechasidagi besh kapsula — har biri cheklangan tirajda
              </p>
            </div>
          </Reveal>

          <div>
            {COLLECTION.map((item, i) => (
              <Reveal key={item.name} reduce={reduce} delay={Math.min(i * 70, 280)}>
                <button
                  type="button"
                  className="atlas-row atlas-collection-row"
                  onClick={() => play("tap")}
                  onMouseEnter={() => play("hover")}
                  style={{
                    display: "grid",
                    width: "100%",
                    textAlign: "left",
                    background: "transparent",
                    color: "inherit",
                    font: "inherit",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "baseline",
                    gap: 24,
                    padding: "24px 20px",
                    borderTop: i === 0 ? `1px solid ${terracotta}55` : "none",
                    borderRight: "none",
                    borderLeft: "none",
                    borderBottom: `1px solid ${terracotta}55`,
                    cursor: "pointer",
                    minHeight: 44,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 26,
                        fontWeight: 400,
                        color: earth,
                        letterSpacing: "0.005em",
                      }}
                    >
                      {item.name}
                    </div>
                    <div
                      style={{
                        marginTop: 6,
                        fontFamily: "var(--font-body)",
                        fontSize: 14,
                        color: `${earth}99`,
                        fontStyle: "italic",
                      }}
                    >
                      {item.note}
                    </div>
                  </div>
                  <div
                    className="atlas-collection-price"
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      color: terracotta,
                      fontFeatureSettings: '"tnum" 1, "lnum" 1',
                      whiteSpace: "nowrap",
                      textAlign: "right",
                    }}
                  >
                    {item.price}
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <div
            style={{
              marginTop: 36,
              textAlign: "center",
              fontFamily: "var(--font-body)",
              fontSize: 13,
              fontStyle: "italic",
              color: `${earth}88`,
            }}
          >
            * Narxlar mato va o'lchamga qarab o'zgaradi · Shaxsiy o'lchov atelyeda
          </div>
        </div>
      </section>

      {/* ─── GALLERY ────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ─── SCHEDULE ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["12:00", "Ribbon-cut — tantanali ochilish marosimi"],
            ["12:30", "Birinchi mehmonlar va ekskursiya"],
            ["14:00", "Cocktail va prosecco"],
            ["16:00", "Trunk show — kollektsiya namoyishi"],
            ["19:00", "After-party — DJ va musiqa"],
          ]}
        />
      </Reveal>

      {/* ─── MAP ────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine="Amir Temur ko'chasi 12 · ATLAS BOUTIQUE · Toshkent"
        />
      </Reveal>

      {/* ─── RSVP ───────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedRsvp.setRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={"Birinchi mehmonlarimiz boʻling"}
        />
      </Reveal>

      {/* ─── SHARE ──────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      <GuestFooter t={t} />

      {/* Hidden interior reference — keeps the second photo materially
          part of the page even if the gallery doesn't reach it. */}
      <div aria-hidden style={{ display: "none" }} data-interior-photo={interiorPhoto} />

      {/* ─── Floating music toggle ──────────────────────────────────── */}
      <button
        type="button"
        className="atlas-music-btn atlas-music-toggle"
        onClick={toggleMusic}
        aria-pressed={music}
        aria-label={music ? "Pause music" : "Play music"}
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 50,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: `1px solid ${earth}`,
          background: music ? terracotta : cream,
          color: music ? cream : earth,
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 18,
          boxShadow: `0 6px 14px ${earth}33`,
          padding: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
          {music ? (
            <g fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </g>
          ) : (
            <g fill="currentColor">
              <path d="M12 4 L18 3 L18 14 A3 3 0 1 1 16 11 L16 6 L14 6.4 L14 16 A3 3 0 1 1 12 13 Z" />
            </g>
          )}
        </svg>
      </button>
    </>
  );
}
